import { getCachedNumberFormatter } from '~/_shared/utils/numberFormatter/numberFormatter';
import type { DemographicValue } from '../templateRenderer/templateRenderer.helpers';

const currencyUnits = ['dollars'];
const percentageUnits = ['percent', '%', 'rate'];

export enum ReportItemFormatType {
  CURRENCY = 'CURRENCY',
  COUNT = 'COUNT',
  PERCENT = 'PERCENT',
  DECIMAL = 'DECIMAL',
}

export const DEFAULT_REPORT_ITEM_FORMAT_TYPE = ReportItemFormatType.COUNT;
const EMPTY_REPORT_VALUE = '—';

const currencyFormatter2dp = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const currencyFormatter0dp = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
const integerFormatter = new Intl.NumberFormat('en-US');

export const isReportDemographicPercentage = (demographicId: string) => {
  return demographicId.includes('%');
};

export const getTemplateRowFormatType = (formatTypeRaw: string) => {
  const formatType = formatTypeRaw.toUpperCase().trim();

  if (Object.values(ReportItemFormatType).includes(formatType as ReportItemFormatType)) {
    return formatType as ReportItemFormatType;
  }

  return DEFAULT_REPORT_ITEM_FORMAT_TYPE;
};

export const getReportItemFormatType = ({ demographicId, format, unit }: { demographicId: string; unit: string; format: string }): ReportItemFormatType => {
  const normalizedUnit = unit.toLowerCase().trim();

  if (isReportDemographicPercentage(demographicId) || percentageUnits.includes(normalizedUnit)) {
    return ReportItemFormatType.PERCENT;
  }

  if (format.includes('$') || currencyUnits.includes(normalizedUnit)) {
    return ReportItemFormatType.CURRENCY;
  }

  if (format.includes('.0')) {
    return ReportItemFormatType.DECIMAL;
  }

  return DEFAULT_REPORT_ITEM_FORMAT_TYPE;
};

export const formatReportValue = ({ value, formatType, unit }: { value: DemographicValue | null; formatType: ReportItemFormatType; unit: string }) => {
  if (typeof value === 'string') {
    return value ? value : EMPTY_REPORT_VALUE;
  }

  if (value === null || isNaN(value)) {
    return EMPTY_REPORT_VALUE;
  }

  switch (formatType) {
    case ReportItemFormatType.PERCENT:
      return formatPercentageValue(value);
    case ReportItemFormatType.CURRENCY:
      return formatCurrency(value);
    case ReportItemFormatType.DECIMAL:
      return formatWithOptionalUnit({ value, unit, formatWithoutUnit: formatDecimalValue });
    case ReportItemFormatType.COUNT:
    default:
      return formatWithOptionalUnit({ value, unit, formatWithoutUnit: formatIntegerValue });
  }
};

const formatCurrency = (value: number): string => {
  const abs = Math.abs(value);

  if (abs >= 1_000_000_000) {
    return `${currencyFormatter2dp.format(value / 1_000_000_000)} B`;
  }

  if (abs >= 1_000_000) {
    return `${currencyFormatter2dp.format(value / 1_000_000)} M`;
  }

  // < 1M - whole dollars, rounded, with commas
  return currencyFormatter0dp.format(value);
};

const formatPercentageValue = (value: number): string => {
  const rounded = Math.round(value * 10) / 10;

  // dash rule: rounds to 0.0%
  if (rounded === 0) {
    return EMPTY_REPORT_VALUE;
  }

  return `${rounded.toFixed(1)}%`;
};

const unitNormalizationMap: Record<string, string> = {
  years: 'yrs',
  'years old': 'yrs',
  yrs: 'yrs',
  persons: '',
  people: '',
  population: '',
  adults: '',
  households: '',
  vehicles: '',
  'sq miles': 'sq mi',
  'sq mile': 'sq mi',
  'square miles': 'sq mi',
  'square mile': 'sq mi',
  'sq mi': 'sq mi',
  'sq km': 'sq km',
  'square km': 'sq km',
  'square kilometers': 'sq km',
  'square kilometer': 'sq km',
  index: '',
  score: '',
  zscore: '',
  coefficient: '',
  minutes: 'min',
  min: 'min',
  feet: 'ft',
  ft: 'ft',
  inches: 'in',
  in: 'in',
  degrees: '°',
  'degrees (f)': '°F',
  '°f': '°F',
  psm: 'psm',
  label: '',
  segment: '',
  segments: '',
  groups: '',
  unknown: '',
  dwellings: '',
  employees: '',
  firms: '',
  rooms: '',
};

const getDisplayUnit = (rawUnit: string): string => {
  const trimmed = rawUnit.trim();
  const normalizedKey = trimmed.toLowerCase();

  return unitNormalizationMap[normalizedKey] ?? trimmed;
};

const formatWithOptionalUnit = ({ value, unit, formatWithoutUnit }: { value: number; unit: string; formatWithoutUnit: (value: number) => string }) => {
  const displayUnit = getDisplayUnit(unit);

  if (!displayUnit) {
    return formatWithoutUnit(value);
  }

  const separator = displayUnit.startsWith('°') ? '' : ' ';
  return `${formatUnitValue(value)}${separator}${displayUnit}`;
};

const formatUnitValue = (value: number): string => {
  const abs = Math.abs(value);

  if (abs >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(2)} M`;
  }
  else {
    const numberFormatter = getCachedNumberFormatter({
      minimumFractionDigits: value % 1 === 0 ? 0 : 1,
      maximumFractionDigits: 2,
    });

    return numberFormatter.format(value);
  }
};

const formatDecimalValue = (value: number): string => {
  const abs = Math.abs(value);

  // >= 1, max 2 decimal places
  if (abs >= 1) {
    const numberFormatter = getCachedNumberFormatter({
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    return numberFormatter.format(value);
  }

  // < 1, up to 4 decimal places
  const rounded = Number(value.toFixed(4));

  // threshold display
  if (rounded === 0) {
    return value < 0 ? '> -0.0001' : '< 0.0001';
  }

  return rounded.toString();
};

const formatIntegerValue = (value: number): string => {
  return integerFormatter.format(Math.round(value));
};
